import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, tokenStore, API } from "../lib/api";
import { Send, Sparkles, User as UserIcon, Bot, Zap, Plus, Trash2, MessageSquare, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";
import { fmtMDTShort } from "../lib/time";

const SESSION_KEY = "conduit_ai_session";
const BASE_SUGGESTIONS = [
  "How is NinjaOne today?",
  "Any compliance risks this week?",
  "How is the ThreatLocker posture?",
];

export default function AskAI() {
  const { user } = useAuth();
  const suggestions = BASE_SUGGESTIONS;
  const focusMentions = "NinjaOne, ThreatLocker, Insights";
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [sessions, setSessions] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const scrollRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const r = await api.get("/ask-ai");
      setSessions(r.data.sessions || []);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    if (!sessionId) { setMessages([]); return; }
    api.get(`/ask-ai/${sessionId}`).then((r) => setMessages(r.data.messages || [])).catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy, streamingText]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    const isNewSession = !sessionId;
    setMessages((m) => [...m, { role: "user", content, ts: new Date().toISOString() }]);
    setBusy(true);
    setStreamingText("");

    try {
      const token = tokenStore.get();
      const resp = await fetch(`${API}/ask-ai/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: content, session_id: sessionId }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";
      let done = false;
      while (!done) {
        const { value, done: d } = await reader.read();
        done = d;
        if (value) {
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop() || "";
          for (const part of parts) {
            const line = part.trim();
            if (!line.startsWith("data:")) continue;
            const payload = line.slice(5).trim();
            try {
              const ev = JSON.parse(payload);
              if (ev.type === "session" && ev.session_id) {
                setSessionId(ev.session_id);
                localStorage.setItem(SESSION_KEY, ev.session_id);
              } else if (ev.type === "delta" && ev.content) {
                full += ev.content;
                setStreamingText(full);
              } else if (ev.type === "done") {
                done = true;
                break;
              }
            } catch {
              /* ignore */
            }
          }
        }
      }
      const finalReply = full.trim();
      setMessages((m) => [...m, { role: "assistant", content: finalReply || "(no reply)", ts: new Date().toISOString() }]);
      // Refresh sidebar so the new session (or updated last_ts) appears at the top
      loadSessions();
      if (isNewSession) {
        // Small delay so backend has persisted the assistant turn
        setTimeout(loadSessions, 400);
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `I couldn't reach Claude: ${e.message}`, ts: new Date().toISOString() }]);
    } finally {
      setStreamingText("");
      setBusy(false);
    }
  };

  const startNewChat = () => {
    setSessionId(null);
    localStorage.removeItem(SESSION_KEY);
    setMessages([]);
    setStreamingText("");
  };

  const openSession = (sid) => {
    if (busy) return;
    setSessionId(sid);
    localStorage.setItem(SESSION_KEY, sid);
    setStreamingText("");
  };

  const deleteSession = async (sid, e) => {
    e?.stopPropagation();
    if (!sid) return;
    if (!window.confirm("Delete this chat? This cannot be undone.")) return;
    setDeletingId(sid);
    try {
      await api.delete(`/ask-ai/${sid}`);
      setSessions((prev) => prev.filter((s) => s.session_id !== sid));
      if (sid === sessionId) startNewChat();
    } catch {
      /* ignore */
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-primary text-[12px] font-semibold uppercase tracking-wider"><Sparkles className="w-3.5 h-3.5" /> Ask AI</div>
          <h1 className="text-[26px] font-semibold tracking-tight mt-1">Your compliance & operations copilot</h1>
          <p className="text-[13px] text-muted-foreground inline-flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5 text-primary" /> Powered by Claude Sonnet 4.5 · grounded on live UC Vigil data
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSidebarOpen((v) => !v)}
            className="h-9 w-9"
            title={sidebarOpen ? "Hide chat history" : "Show chat history"}
            data-testid="askai-toggle-sidebar-btn"
          >
            {sidebarOpen ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
          </Button>
          <Button onClick={startNewChat} className="h-9 gap-1.5" data-testid="askai-new-chat-btn">
            <Plus className="w-4 h-4" /> New chat
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="flex h-[64vh]">
          {/* Sidebar */}
          {sidebarOpen && (
            <aside
              data-testid="askai-sidebar"
              className="w-64 shrink-0 border-r border-border bg-muted/20 flex flex-col"
            >
              <div className="px-3 py-3 border-b border-border flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                  Chat history
                </div>
                <span className="text-[11px] text-muted-foreground" data-testid="askai-sidebar-count">
                  {sessions.length}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto tabler-scroll py-1" data-testid="askai-sidebar-list">
                {sessions.length === 0 && (
                  <div className="px-4 py-6 text-center text-[12.5px] text-muted-foreground">
                    No past chats yet. Ask something to get started.
                  </div>
                )}
                {sessions.map((s) => {
                  const active = s.session_id === sessionId;
                  return (
                    <div
                      key={s.session_id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openSession(s.session_id)}
                      onKeyDown={(e) => { if (e.key === "Enter") openSession(s.session_id); }}
                      data-testid={`askai-session-item-${s.session_id}`}
                      className={cn(
                        "group mx-2 my-1 px-2.5 py-2 rounded-lg cursor-pointer border transition-colors",
                        active
                          ? "bg-primary/10 border-primary/30"
                          : "border-transparent hover:bg-muted/60 hover:border-border"
                      )}
                    >
                      <div className="flex items-start gap-2">
                        <MessageSquare className={cn("w-3.5 h-3.5 mt-0.5 shrink-0", active ? "text-primary" : "text-muted-foreground")} />
                        <div className="flex-1 min-w-0">
                          <div className={cn("text-[13px] leading-snug truncate", active ? "font-semibold" : "font-medium")}>
                            {s.title || "New chat"}
                          </div>
                          <div className="mt-0.5 text-[11px] text-muted-foreground flex items-center justify-between gap-2">
                            <span className="truncate">{fmtMDTShort(s.last_ts)}</span>
                            <span className="shrink-0">{s.message_count} msg</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => deleteSession(s.session_id, e)}
                          disabled={deletingId === s.session_id}
                          title="Delete chat"
                          data-testid={`askai-delete-session-${s.session_id}`}
                          className={cn(
                            "opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity",
                            "p-1 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive",
                            deletingId === s.session_id && "opacity-100"
                          )}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </aside>
          )}

          {/* Main chat pane */}
          <div className="flex-1 flex flex-col min-w-0">
            <div ref={scrollRef} className="flex-1 overflow-y-auto tabler-scroll px-4 sm:px-6 py-6 space-y-4">
              {messages.length === 0 && !streamingText && (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="mt-3 text-[16px] font-semibold">How can I help today?</h3>
                  <p className="text-[13px] text-muted-foreground max-w-md">Ask about {focusMentions} — or any active alert.</p>
                  <div className="mt-4 flex flex-wrap gap-2 justify-center">
                    {suggestions.map((s) => (
                      <button key={s} onClick={() => send(s)} className="text-[12.5px] rounded-full border border-border bg-muted/40 hover:bg-primary/10 hover:border-primary/40 px-3 py-1.5">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={cn("flex gap-3", m.role === "user" ? "justify-end" : "justify-start")}>
                  {m.role === "assistant" && (
                    <div className="w-8 h-8 shrink-0 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center"><Bot className="w-4 h-4 text-primary" /></div>
                  )}
                  <div className={cn("max-w-[70%] rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap",
                    m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted text-foreground rounded-bl-sm")}>
                    {m.content}
                  </div>
                  {m.role === "user" && (
                    <div className="w-8 h-8 shrink-0 rounded-lg bg-muted border border-border flex items-center justify-center"><UserIcon className="w-4 h-4" /></div>
                  )}
                </div>
              ))}
              {streamingText && (
                <div className="flex gap-3 justify-start">
                  <div className="w-8 h-8 shrink-0 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center"><Bot className="w-4 h-4 text-primary" /></div>
                  <div className="max-w-[70%] rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap bg-muted text-foreground rounded-bl-sm">
                    {streamingText}
                    <span className="inline-block w-1.5 h-4 bg-primary ml-0.5 align-middle animate-pulse" />
                  </div>
                </div>
              )}
              {busy && !streamingText && (
                <div className="flex gap-3 items-center">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center"><Bot className="w-4 h-4 text-primary" /></div>
                  <div className="flex gap-1 items-center bg-muted rounded-2xl px-3 py-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
            </div>
            <div className="border-t border-border p-3 bg-background/60">
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2">
                <Textarea value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={`Ask about ${focusMentions}…`}
                  rows={1} className="min-h-[42px] max-h-40 resize-none"
                  data-testid="askai-input" />
                <Button type="submit" disabled={busy || !input.trim()} className="h-[42px] gap-1.5" data-testid="askai-send-btn"><Send className="w-4 h-4" /> Send</Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
